import { useState } from 'react';

import clsx from 'clsx';
import { ArrowLeft, Mail, Search, X } from 'lucide-react';

import matchesDemoSearch from './matches-demo-search';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const MESSAGES: readonly Message[] = [
  {
    id: 'pine-confirmed',
    from: 'Pine Ridge Cabins',
    date: 'Today',
    subject: 'Your reservation is confirmed',
    snippet: 'Cabin 4, Friday to Sunday. Check-in after 3 PM.',
    attachment: true,
  },
  {
    id: 'pine-rules',
    from: 'Pine Ridge Cabins',
    date: 'Today',
    subject: 'House rules',
    snippet: 'Quiet hours start at 10 PM. The cabin has a wood stove.',
    attachment: true,
  },
  {
    id: 'ana-options',
    from: 'Ana Ruiz',
    date: 'Sep 28',
    subject: 'Big Sur options',
    snippet: 'Two cabins left for that weekend. Screenshots attached.',
    attachment: true,
  },
  {
    id: 'parks-permit',
    from: 'California State Parks',
    date: 'Sep 27',
    subject: 'Your day-use permit',
    snippet: 'Pfeiffer Falls trail, Saturday, October 10. Show the PDF at the gate.',
    attachment: true,
  },
  {
    id: 'northline',
    from: 'Northline Auto',
    date: 'Sep 26',
    subject: 'Service appointment Thursday',
    snippet: 'Your car is booked for 3 PM. The estimate is attached.',
    attachment: true,
  },
  {
    id: 'ana-reply',
    from: 'Ana Ruiz',
    date: 'Sep 25',
    subject: 'Re: Big Sur?',
    snippet: 'I’m in. Maybe a cabin near the river instead of camping?',
    attachment: false,
  },
  {
    id: 'jordan-lunch',
    from: 'Jordan Lee',
    date: 'Sep 24',
    subject: 'Lunch Wednesday',
    snippet: 'Noon works. The place by the office?',
    attachment: false,
  },
  {
    id: 'pine-inquiry',
    from: 'Pine Ridge Cabins',
    date: 'Sep 22',
    subject: 'Thanks for your inquiry',
    snippet: 'We have availability October 9 to 11. Reply to hold a cabin.',
    attachment: false,
  },
];

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type Message = {
  id: string;
  from: string;
  date: string;
  subject: string;
  snippet: string;
  attachment: boolean;
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

/** The search palette with a `has:attachment` filter; typing filters a fixed mailbox. */
const SearchMockup: React.FC<{ className?: string }> = ({ className }) => {
  const [query, setQuery] = useState<string>('cabin');
  const [hasToken, setHasToken] = useState<boolean>(true);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openedId, setOpenedId] = useState<string | null>(null);

  const needle = query.trim().toLowerCase();
  const results = MESSAGES.filter((message, index) =>
    matchesDemoSearch(
      { ...message, unread: index < 3, starred: index === 1 },
      `${hasToken ? 'has:attachment ' : ''}${query}`
    )
  );
  const opened = MESSAGES.find((message) => message.id === openedId);
  const selected = results.find(({ id }) => id === selectedId) ?? results[0];
  const activeId = hoveredId ?? selected?.id;

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Keep typing here from driving the page's keyboard demo.
    event.stopPropagation();
    if (event.key === 'Backspace' && query === '' && hasToken) {
      event.preventDefault();
      setHasToken(false);
    } else if (event.key === 'Enter' && selected) {
      event.preventDefault();
      setOpenedId(selected.id);
    } else if (event.key === 'Escape') {
      setOpenedId(null);
    } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && selected) {
      event.preventDefault();
      const index = results.indexOf(selected) + (event.key === 'ArrowDown' ? 1 : -1);
      setSelectedId(results[(index + results.length) % results.length].id);
      setHoveredId(null);
    }
  };

  return (
    <div
      className={clsx(
        'bg-gray-2 border-gray-6 text-gray-12 w-[600px] overflow-hidden rounded-lg border text-left shadow-[0_20px_25px_-5px_rgba(0,0,0,.1),0_8px_10px_-6px_rgba(0,0,0,.1)]',
        className
      )}
    >
      {opened ? (
        <article className="flex h-[286px] flex-col gap-3 p-4">
          <button
            type="button"
            aria-label="Back to search results"
            className="text-gray-11 flex w-fit items-center gap-2 text-sm"
            onClick={() => setOpenedId(null)}
          >
            <ArrowLeft className="size-4" />
            Search results
          </button>
          <h2 className="font-medium">{opened.subject}</h2>
          <p className="text-gray-11 text-sm">
            {opened.from} · {opened.date}
          </p>
          <p className="text-sm">{opened.snippet}</p>
          {opened.attachment ? (
            <a
              className="border-gray-6 w-fit rounded border px-3 py-2 text-sm"
              download="attachment.txt"
              href={`data:text/plain,${encodeURIComponent(opened.snippet)}`}
            >
              Download attachment
            </a>
          ) : null}
        </article>
      ) : (
        <>
          <label className="border-gray-6 flex h-[46px] cursor-text items-center gap-2 border-b px-3 text-sm">
            <Search className="text-gray-11 size-4 shrink-0" />
            {hasToken ? (
              <span className="outline-gray-7 bg-gray-3 inline-flex h-[22px] shrink-0 items-center gap-0.5 rounded px-1 font-medium outline">
                <i className="text-gray-11 not-italic">has:</i>attachment
                <button
                  type="button"
                  aria-label="Remove attachment filter"
                  className="ml-1 grid size-5 place-items-center"
                  onClick={(event) => {
                    event.preventDefault();
                    setHasToken(false);
                  }}
                >
                  <X className="size-3" />
                </button>
              </span>
            ) : null}
            <input
              className="placeholder:text-gray-11 h-full min-w-0 flex-1 bg-transparent outline-none"
              value={query}
              aria-label="Search mail"
              placeholder="Search mail"
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
            />
          </label>
          <div className="text-gray-11 flex justify-between px-3 pt-2 pb-1 text-xs font-medium">
            <span>Search results</span>
            <span className="font-normal">
              {results.length} {results.length === 1 ? 'result' : 'results'}
            </span>
          </div>
          <div
            className="h-[198px] [scrollbar-width:none] overflow-y-auto pb-1.5"
            onMouseLeave={() => setHoveredId(null)}
          >
            {results.length === 0 ? (
              <p className="text-gray-11 flex h-full items-center justify-center text-sm">
                No results
              </p>
            ) : (
              results.map((message) => (
                <button
                  key={message.id}
                  type="button"
                  className={clsx(
                    'mx-1 flex w-[calc(100%-8px)] gap-2 rounded-md px-2 py-1.5 text-left',
                    message.id === activeId && 'bg-gray-4'
                  )}
                  onMouseEnter={() => setHoveredId(message.id)}
                  onClick={() => setSelectedId(message.id)}
                  onDoubleClick={() => setOpenedId(message.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      setOpenedId(message.id);
                    }
                  }}
                >
                  <Mail className="text-gray-11 mt-0.5 size-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="grid grid-cols-[minmax(0,1fr)_104px] text-sm leading-5 font-medium">
                      <span className="truncate">{message.from}</span>
                      <span className="text-gray-11 text-right text-xs font-normal">
                        {message.date}
                      </span>
                    </div>
                    <div className="truncate text-xs leading-4 font-medium">
                      {highlight(message.subject, needle)}
                    </div>
                    <div className="text-gray-11 truncate text-xs leading-4">
                      {highlight(message.snippet, needle)}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Wraps each case-insensitive occurrence of `needle` in a highlight. */
const highlight = (text: string, needle: string): React.ReactNode => {
  if (!needle) return text;
  const lower = text.toLowerCase();
  const parts: React.ReactNode[] = [];
  let from = 0;
  for (let at = lower.indexOf(needle); at !== -1; at = lower.indexOf(needle, from)) {
    parts.push(text.slice(from, at));
    parts.push(
      <mark key={at} className="bg-blue-9 rounded-[2px] px-px text-white">
        {text.slice(at, at + needle.length)}
      </mark>
    );
    from = at + needle.length;
  }
  parts.push(text.slice(from));
  return parts;
};

export default SearchMockup;
